import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Terminal, AtSign, Hash, Sparkles, ListChecks, Wrench, Clock } from 'lucide-react';
import { Api } from '../../lib/api';
import { useSearch, useTypes, qk } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { fuzzyScore } from '../../lib/fuzzy';
import { recordUse, usageBoost } from '../../lib/usage';
import { sanitizeSnippet } from '../links/BacklinksPanel';
import { TypeDot } from '../ui/TypeDot';
import { NewNoteDialog } from '../dialogs/NewNoteDialog';
import { ToolRunDialog } from '../agent/ToolRunDialog';
import type { ToolInfo } from '../../lib/api';

type Mode = 'all' | 'cmd' | 'ask' | 'entity' | 'tag' | 'runbook' | 'tool';
const PREFIX: Record<string, Mode> = { '>': 'cmd', '?': 'ask', '@': 'entity', '#': 'tag', '/': 'runbook', '!': 'tool' };
const MODE_LABEL: Record<Mode, string> = { all: 'search', cmd: 'command', ask: 'ask agent', entity: 'entity', tag: 'tag', runbook: 'runbook', tool: 'tool' };
const ENTITY_TYPES = new Set(['site', 'device', 'link', 'circuit', 'person', 'vendor']);

interface Item {
  id: string;
  kind: 'note' | 'command' | 'recent' | 'info';
  title: string;
  sub?: string;
  type?: string;
  path?: string;
  snippet?: string;
  preview?: string;
  tags?: string[];
  disabled?: boolean;
  run?: (opts: { split: boolean }) => void;
}

export interface Command {
  id: string;
  title: string;
  hint?: string;
  run: () => void;
}

export function CommandPalette({ commands }: { commands: Command[] }) {
  const open = useUi((s) => s.paletteOpen);
  const seed = useUi((s) => s.paletteSeed);
  const close = useUi((s) => s.closePalette);
  const openNote = useUi((s) => s.openNote);
  const recentIds = useUi((s) => s.recentNoteIds);
  const [text, setText] = useState(seed);
  const [sel, setSel] = useState(0);
  const [newNote, setNewNote] = useState<string | null>(null);
  const [runTool, setRunTool] = useState<ToolInfo | null>(null);
  const setDockTab = useUi((s) => s.setDockTab);
  const tools = useQuery({ queryKey: ['tools'], queryFn: Api.agent.tools, enabled: open && text.startsWith('!'), staleTime: 60_000 });
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const types = useTypes();
  const qc = useQueryClient();

  // Focus synchronously on open (layout effect + autoFocus) so keystrokes typed right after
  // Ctrl+K never leak into the editor underneath. The palette is a plain overlay: no transition.
  useLayoutEffect(() => {
    if (open) {
      setText(seed);
      setSel(0);
      input.current?.focus();
    }
  }, [open, seed]);

  const mode: Mode = PREFIX[text[0]] ?? 'all';
  const query = mode === 'all' ? text : text.slice(1);
  const debounced = useDebounced(query, 40);

  const search = useSearch(
    mode === 'tag' ? `#${debounced.trim().split(/\s+/)[0]}` : debounced,
    { limit: mode === 'entity' ? 40 : 30, prefix: true },
    open && (mode === 'all' || mode === 'entity' || (mode === 'tag' && debounced.trim().length > 0)),
  );
  const recent = useQuery({
    queryKey: ['recent-notes', recentIds.slice(0, 8)],
    queryFn: async () => (await Promise.all(recentIds.slice(0, 8).map((id) => Api.notes.get(id).catch(() => null)))).filter((n): n is NonNullable<typeof n> => !!n),
    enabled: open && mode === 'all',
    staleTime: 30_000,
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    const q = query.trim();
    if (mode === 'cmd') {
      return commands
        .map((c) => ({ c, s: fuzzyScore(q, c.title) }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .map(({ c }) => ({ id: `cmd:${c.id}`, kind: 'command' as const, title: c.title, sub: c.hint, run: () => c.run() }));
    }
    if (mode === 'ask') {
      const ask = (t: string) => () => {
        setDockTab('agent');
        setTimeout(() => document.dispatchEvent(new CustomEvent('cortex:ask', { detail: t })), 60);
      };
      if (!q) return [{ id: 'ask-open', kind: 'command', title: 'Open the agent panel', sub: 'Ctrl+J', run: ask('') }];
      return [{ id: 'ask', kind: 'command', title: `Ask: ${q}`, sub: 'agent', run: ask(q) }];
    }
    if (mode === 'runbook') return [{ id: 'rb', kind: 'info', title: 'Run a runbook against an entity — Phase 6', sub: 'not built', disabled: true }];
    if (mode === 'tool') {
      return (tools.data?.tools ?? [])
        .map((t) => ({ t, s: q ? Math.max(fuzzyScore(q, t.name), fuzzyScore(q, t.description) - 20) : 1 }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .map(({ t }) => ({ id: `tool:${t.name}`, kind: 'command' as const, title: t.name, sub: `${t.riskLevel.toLowerCase()}${t.policy.requiresApproval ? ' · approval' : ''}${t.policy.enabled ? '' : ' · disabled'}`, snippet: t.description, disabled: !t.policy.enabled, run: () => setRunTool(t) }));
    }

    if (mode === 'all') {
      if (!q) {
        for (const n of recent.data ?? []) out.push({ id: n.id, kind: 'recent', title: n.title, path: n.path, type: n.type, preview: n.preview ?? undefined, tags: n.tags });
      } else {
        // Commands that fuzzy-match sneak in at the bottom in unified mode.
        const cmdHits = commands.map((c) => ({ c, s: fuzzyScore(q, c.title) })).filter((x) => x.s > 20).sort((a, b) => b.s - a.s).slice(0, 3);
        for (const n of recent.data ?? []) if (fuzzyScore(q, n.title) > 10) out.push({ id: n.id, kind: 'recent', title: n.title, path: n.path, type: n.type, preview: n.preview ?? undefined, tags: n.tags });
        for (const { c } of cmdHits) out.push({ id: `cmd:${c.id}`, kind: 'command', title: c.title, sub: c.hint, run: () => c.run() });
      }
    }
    const hits = (search.data?.hits ?? []).filter((h) => (mode === 'entity' ? ENTITY_TYPES.has(h.type) : true));
    const seen = new Set(out.map((i) => i.id));
    const ranked = hits
      .map((h, i) => ({ h, s: (hits.length - i) / hits.length + usageBoost(h.id) * 1.5 }))
      .sort((a, b) => b.s - a.s);
    for (const { h } of ranked) {
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      out.push({ id: h.id, kind: 'note', title: h.title, path: h.path, type: h.type, snippet: h.snippet, tags: h.tags });
    }
    const looksLikeTitle = q.length > 0 && !/[:#"*]/.test(q) && !/^-/.test(q);
    if (mode === 'all' && looksLikeTitle && !out.some((i) => i.kind === 'note' && i.title.toLowerCase() === q.toLowerCase())) {
      out.push({ id: 'new', kind: 'command', title: `Create note "${q}"`, sub: 'new', run: () => setNewNote(q) });
    }
    return out;
  }, [mode, query, commands, search.data, recent.data, tools.data, setDockTab]);

  useEffect(() => setSel((s) => Math.min(s, Math.max(0, items.length - 1))), [items.length]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('.p-item.sel')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open) return null;

  const activate = (it: Item, split: boolean) => {
    if (it.disabled) return;
    if (it.kind === 'note' || it.kind === 'recent') {
      recordUse(it.id);
      openNote(it.id, { split });
      close();
      return;
    }
    it.run?.({ split });
    if (it.id !== 'new' && !it.id.startsWith('tool:')) close();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') (e.preventDefault(), setSel((s) => Math.min(items.length - 1, s + 1)));
    else if (e.key === 'ArrowUp') (e.preventDefault(), setSel((s) => Math.max(0, s - 1)));
    else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[sel];
      if (it) activate(it, e.ctrlKey || e.metaKey);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const it = items[sel];
      if (it && (it.kind === 'note' || it.kind === 'recent')) setText((mode === 'all' ? '' : text[0]) + it.title);
    } else if (e.key === 'Escape') close();
  };

  const current = items[sel];
  const typeLabel = (t?: string) => types.data?.find((x) => x.key === t)?.label ?? t;

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="palette panel ticks" role="dialog" aria-label="Command palette">
        <div className="palette-input">
          <span className="mode">{MODE_LABEL[mode]}</span>
          <input
            ref={input}
            type="text"
            value={text}
            onChange={(e) => (setText(e.target.value), setSel(0))}
            onKeyDown={onKey}
            placeholder="Search notes…  > commands  @ entities  # tags  ? ask  / runbooks  ! tools"
            spellCheck={false}
            autoComplete="off"
            autoFocus
          />
          {search.isFetching && <span className="light teal" title="searching" />}
          {search.data && query && <span className="mono" style={{ color: 'var(--text-faint)' }}>{search.data.total} · {search.data.tookMs}ms</span>}
        </div>
        <div className="palette-body">
          <div className="palette-list" ref={listRef}>
            {items.length === 0 && <div className="p-group" style={{ color: 'var(--text-faint)' }}>{query ? 'No matches.' : 'Type to search. Recent notes appear here once you open some.'}</div>}
            {items.map((it, i) => (
              <div
                key={it.id}
                className={`p-item${i === sel ? ' sel' : ''}${it.disabled ? ' disabled' : ''}`}
                onMouseEnter={() => setSel(i)}
                onClick={(e) => activate(it, e.ctrlKey || e.metaKey)}
              >
                <span className="ico">
                  {it.kind === 'command' ? <Terminal size={13} /> : it.kind === 'recent' ? <Clock size={13} /> : it.kind === 'info' ? modeIcon(mode) : it.type ? <TypeDot type={it.type} /> : <FileText size={13} />}
                </span>
                <span className="t">{it.title}</span>
                <span className="sub">{it.sub ?? (it.type ? typeLabel(it.type) : '')}</span>
              </div>
            ))}
          </div>
          <div className="palette-preview">
            {current && (current.kind === 'note' || current.kind === 'recent') ? (
              <>
                <div className="title">{current.title}</div>
                <div className="meta">
                  {current.type && <span className="chip teal">{typeLabel(current.type)}</span>}
                  {current.tags?.map((t) => (
                    <span key={t} className="chip">#{t}</span>
                  ))}
                </div>
                <div className="mono" style={{ marginBottom: 10, color: 'var(--text-faint)' }}>{current.path}</div>
                {current.snippet ? <div className="snippet" dangerouslySetInnerHTML={{ __html: sanitizeSnippet(current.snippet) }} /> : <div className="snippet">{current.preview}</div>}
              </>
            ) : current?.kind === 'command' ? (
              <div className="snippet">{current.snippet ?? current.title}</div>
            ) : (
              <div className="snippet" style={{ color: 'var(--text-faint)' }}>
                {mode === 'all' && 'Unified search across titles, headings, body, tags and frontmatter. Field filters: type:device site:"South Walker" #tag -excluded "exact phrase".'}
                {mode === 'entity' && 'Sites, devices, links, circuits, people, vendors.'}
                {mode === 'tag' && 'Notes carrying a tag.'}
                {mode === 'cmd' && 'Commands.'}
                {mode === 'ask' && 'Ask Cortex. The open note is sent as context; answers cite notes and land in the Agent tab of the dock.'}
                {mode === 'tool' && 'Run a tool directly: calculators, vault reads, web fetch. Approval-gated tools ask first. Every run is logged.'}
              </div>
            )}
          </div>
          <div className="palette-foot">
            <span><span className="kbd">↑↓</span> navigate</span>
            <span><span className="kbd">↵</span> open</span>
            <span><span className="kbd">Ctrl ↵</span> open in split</span>
            <span><span className="kbd">Tab</span> complete</span>
            <span><span className="kbd">Esc</span> close</span>
          </div>
        </div>
      </div>
      {runTool && (
        <ToolRunDialog
          tool={runTool}
          onClose={() => {
            setRunTool(null);
            close();
          }}
        />
      )}
      {newNote !== null && (
        <NewNoteDialog
          initialTitle={newNote}
          onClose={() => {
            setNewNote(null);
            close();
            qc.invalidateQueries({ queryKey: qk.tree });
          }}
        />
      )}
    </div>
  );
}

function modeIcon(m: Mode) {
  switch (m) {
    case 'ask': return <Sparkles size={13} />;
    case 'entity': return <AtSign size={13} />;
    case 'tag': return <Hash size={13} />;
    case 'runbook': return <ListChecks size={13} />;
    case 'tool': return <Wrench size={13} />;
    default: return <FileText size={13} />;
  }
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}
